import { ReactElement, useState } from 'react';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import SearchInput from '../controls/SearchInput';
import Filter from '../controls/Filter';
import DevicesTable from './DevicesTable';
import AddDeviceModal from '../modals/AddDevice';
import AddGroupModal from '../modals/AddGroup';
import { useData } from '../context/data.context';
import Loader from './loader';
import { ActionData, DeviceAttributes } from '../constants/telosair-interfaces';
import { TOAST_MESSAGES } from '../constants/global-constants';
import { changeDeviceStatus, deleteDevice } from '../services/dashboard-service';

/**
 * Devices component renders the list of devices to add or edit.
 * @param root0 - Properties of DevicesTab component.
 * @param root0.addNew - Condition to show add modal or not.
 * @returns - Devices Component.
 */
export default function DevicesTab({ addNew }: { addNew: boolean }): ReactElement {
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(addNew);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const { devices, groupedData: groups, loading, fetchAllDevices } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [actionData, setActionData] = useState<ActionData<DeviceAttributes>>(
    {} as ActionData<DeviceAttributes>
  );
  /**
   * Handle close the Create Group modal.
   * @param newData - Check weather new group is created.
   */
  const handleGroupModalClose = (newData?: boolean): void => {
    if (newData) {
      fetchAllDevices();
    }
    setIsGroupModalOpen(false);
  };

  /**
   * Function to handle Device Modal.
   * @param newDeviceAdded - To check weather a New device is added or not.
   */
  const handleDeviceModalClose = (newDeviceAdded: boolean): void => {
    if (newDeviceAdded) {
      fetchAllDevices();
    }
    setIsModalOpen(false);
    setActionData({} as ActionData<DeviceAttributes>);
  };

  /**
   * Function to delete a group.
   * @param id - ID of the group.
   */
  const handleDeleteDevice = async (id: number): Promise<void> => {
    try {
      setIsLoading(true);
      await deleteDevice(id);
      await fetchAllDevices();
      setIsLoading(false);
      toast.success(TOAST_MESSAGES.deleted);
    } catch (error) {
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
   * Function to delete a group.
   * @param action - Device action data.
   */
  const handleChangeDeviceStatus = async (action: ActionData<DeviceAttributes>): Promise<void> => {
    try {
      setIsLoading(true);
      const updatedSTatus = action.data.status === 'active' ? 'inactive' : 'active';
      await changeDeviceStatus(action.data.id, updatedSTatus);
      await fetchAllDevices();
      setIsLoading(false);
      toast.success(TOAST_MESSAGES.edited);
    } catch (error) {
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
   * Function to operate delete and edit group.
   * @param action - Action data needs type and group data.
   */
  const handleAction = async (action: ActionData<DeviceAttributes>): Promise<void> => {
    if (action.type === 'delete') {
      handleDeleteDevice(action.data.id);
    } else if (action.type === 'change-status') {
      handleChangeDeviceStatus(action);
    } else {
      setActionData(action);
      setIsModalOpen(true);
    }
  };

  return (
    <div className="relative flex flex-1 flex-col">
      <div className="flex flex-col sm:flex-row gap-4 justify-between rounded bg-white dark:bg-container-500 p-4 mt-4">
        <div className="flex items-center space-x-4 invisible">
          <SearchInput textValue={searchText} setText={setSearchText} />
          <Filter position="left" />
        </div>
        <div className="mr-4">
          <Button
            theme="primary"
            size="sm"
            name="Add Device"
            onClick={() => {
              setIsModalOpen(true);
            }}
            isBlockButton
          />
        </div>
      </div>
      {(loading || isLoading) && <Loader />}
      <div className="mt-4">
        {/* {main content} */}
        <DevicesTable handleAction={handleAction} devices={devices} />
      </div>
      {/* {Add Device/Edit Device} */}
      {isModalOpen && (
        <AddDeviceModal
          actionData={actionData}
          groups={groups}
          setIsGroupModalOpen={setIsGroupModalOpen}
          handleClose={handleDeviceModalClose}
        />
      )}

      {/* {Add a new group} */}
      {isGroupModalOpen && <AddGroupModal handleClose={handleGroupModalClose} />}
    </div>
  );
}
